import { mergeAttributes, Node } from "@tiptap/core"
import { Fragment, Slice } from "@tiptap/pm/model"
import { Plugin, PluginKey } from "@tiptap/pm/state"

import { findNodeAroundSelection } from "@/tiptap/lib/tiptap-utils"
import { FOCUS_NO_SCROLL } from "@/tiptap/lib/tiptap-utils"

export const CALLOUT_VARIANTS = ["info", "success", "warning", "danger"]

// Callouts only allow simple text content: paragraphs, headings, and lists.
export const CALLOUT_ALLOWED_CONTENT = ["paragraph", "heading", "bulletList", "orderedList", "taskList"]

// Structural children of the allowed list types are also kept when filtering pasted content.
const CALLOUT_ALLOWED_NODES = new Set([...CALLOUT_ALLOWED_CONTENT, "listItem", "taskItem"])

export const CALLOUT_PASTE_FILTER_KEY = new PluginKey("calloutPasteFilter")

// Mirrors code block paste behavior: disallowed nodes are reduced to their
// plain text (as paragraphs) instead of splitting the callout apart.
function filterNodeForCallout(node, schema) {
  if (node.isText || node.isInline) return [node]

  if (!CALLOUT_ALLOWED_NODES.has(node.type.name)) {
    const text = node.textBetween(0, node.content.size, " ", " ").trim()
    return text ? [schema.nodes.paragraph.create(null, schema.text(text))] : []
  }

  const children = []
  node.forEach((child) => {
    children.push(...filterNodeForCallout(child, schema))
  })

  return [node.copy(Fragment.fromArray(children))]
}

export function filterSliceForCallout(slice, schema) {
  const filtered = []
  slice.content.forEach((node) => {
    filtered.push(...filterNodeForCallout(node, schema))
  })

  const fragment = Fragment.fromArray(filtered)
  if (fragment.eq(slice.content)) return slice

  return Slice.maxOpen(fragment)
}

function normalizeVariant(variant) {
  return CALLOUT_VARIANTS.includes(variant) ? variant : "info"
}

function extractVariantFromClassName(className = "") {
  const matchedVariant = CALLOUT_VARIANTS.find((variant) =>
    className.split(/\s+/).includes(`callout-${variant}`))

  return matchedVariant || "info"
}

function paragraphsFromCodeBlockText(text, schema) {
  const lines = text.length ? text.split("\n") : [""]

  return Fragment.fromArray(
    lines.map((line) =>
      schema.nodes.paragraph.create(null, line.length ? schema.text(line) : undefined),
    ),
  )
}

// Callouts cannot contain code blocks, so converting must replace the code
// block with a callout and move its plain text into paragraphs.
function replaceCodeBlockWithCallout({ state, dispatch, calloutType, variant }) {
  const codeBlock = findNodeAroundSelection(state, "codeBlock")
  if (!codeBlock) return false

  const callout = calloutType.create(
    { variant },
    paragraphsFromCodeBlockText(codeBlock.node.textContent, state.schema),
  )

  if (dispatch) {
    dispatch(
      state.tr.replaceWith(codeBlock.pos, codeBlock.pos + codeBlock.node.nodeSize, callout),
    )
  }

  return true
}

export const Callout = Node.create({
  name: "callout",
  priority: 1000,
  group: "block",
  content: `(${CALLOUT_ALLOWED_CONTENT.join(" | ")})+`,
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "info",
        parseHTML: (element) => extractVariantFromClassName(element.getAttribute("class") || ""),
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "div.callout",
        getAttrs: (element) => ({
          variant: extractVariantFromClassName(element.getAttribute("class") || ""),
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const variant = normalizeVariant(node.attrs.variant)

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        class: `callout callout-${variant}`,
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setCallout:
        (attrs = {}) =>
          ({ chain, editor, state, dispatch }) => {
            const variant = normalizeVariant(attrs.variant)

            if (editor.isActive(this.name)) {
              return chain()
                .focus()
                .updateAttributes(this.name, { variant })
                .run()
            }

            // Code blocks are not valid callout content, so unwrap their text
            // into paragraphs inside a new callout instead of wrapping.
            if (editor.isActive("codeBlock")) {
              return replaceCodeBlockWithCallout({
                state,
                dispatch,
                calloutType: this.type,
                variant,
              })
            }

            let commandChain = chain().focus(undefined, FOCUS_NO_SCROLL)

            // Match code block behavior: drop heading styling so the callout
            // wraps a plain paragraph instead of keeping the heading node.
            if (editor.isActive("heading")) {
              commandChain = commandChain.setNode("paragraph")
            }

            return commandChain
              .wrapIn(this.name, {
                variant,
              })
              .run()
          },

      unsetCallout:
        () =>
          ({ chain }) =>
            chain().focus(undefined, FOCUS_NO_SCROLL).lift(this.name).run(),
    }
  },

  addProseMirrorPlugins() {
    const calloutType = this.type

    return [
      new Plugin({
        key: CALLOUT_PASTE_FILTER_KEY,
        props: {
          transformPasted: (slice, view) => {
            const { $from } = view.state.selection

            let insideCallout = false
            for (let depth = $from.depth; depth > 0; depth--) {
              if ($from.node(depth).type === calloutType) {
                insideCallout = true
                break
              }
            }

            if (!insideCallout) return slice

            return filterSliceForCallout(slice, view.state.schema)
          },
        },
      }),
    ]
  },
})

export default Callout
